import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { lazy, Suspense } from "react";

const Landing      = lazy(() => import("./pages/Landing"));
const Login        = lazy(() => import("./pages/Login"));
const Register     = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword  = lazy(() => import("./pages/ResetPassword"));
const Dashboard    = lazy(() => import("./pages/Dashboard"));
const Clients      = lazy(() => import("./pages/Clients"));
const Leads        = lazy(() => import("./pages/Leads"));
const Pricing      = lazy(() => import("./pages/Pricing"));
const ConnectMeta  = lazy(() => import("./pages/ConnectMeta"));
const BotBuilder   = lazy(() => import("./pages/BotBuilder"));
const Conversations = lazy(() => import("./pages/Conversations"));
const Reels        = lazy(() => import("./pages/Reels"));
const Admin        = lazy(() => import("./pages/Admin"));
const Templates    = lazy(() => import("./pages/Templates"));
const Terms        = lazy(() => import("./pages/Terms"));
const Privacy      = lazy(() => import("./pages/Privacy"));
const Refund       = lazy(() => import("./pages/Refund"));
const Contact      = lazy(() => import("./pages/Contact"));

const PageLoader = () => (
  <div style={{ minHeight: "100vh", background: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "#00ff87", fontSize: "18px" }}>
    ⚡ Loading...
  </div>
);

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{
      minHeight: "100vh", background: "#0a0a0a",
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "#00ff87", fontSize: "20px"
    }}>
      ⚡ Loading...
    </div>
  );
  if (!user) return <Navigate to="/login" />;
  if (user.plan === "none") return <Navigate to="/pricing" />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/dashboard" /> : children;
}

export default function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <BrowserRouter>
          <Toaster
            position="top-right"
            toastOptions={{ style: { background: "#1a1a1a", color: "#fff", border: "1px solid #333" } }}
          />
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
            <Route path="/login"            element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register"         element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/forgot-password"  element={<PublicRoute><ForgotPassword /></PublicRoute>} />
            <Route path="/reset-password"   element={<PublicRoute><ResetPassword /></PublicRoute>} />
            <Route path="/dashboard"        element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/clients"          element={<PrivateRoute><Clients /></PrivateRoute>} />
            <Route path="/leads"            element={<PrivateRoute><Leads /></PrivateRoute>} />
            <Route path="/pricing"          element={<Pricing />} />
            <Route path="/connect-meta"     element={<PrivateRoute><ConnectMeta /></PrivateRoute>} />
            <Route path="/bot-builder"      element={<PrivateRoute><BotBuilder /></PrivateRoute>} />
            <Route path="/conversations"    element={<PrivateRoute><Conversations /></PrivateRoute>} />
            <Route path="/reels"            element={<PrivateRoute><Reels /></PrivateRoute>} />
            <Route path="/templates"        element={<PrivateRoute><Templates /></PrivateRoute>} />
            <Route path="/admin-panel"      element={<Admin />} />
            <Route path="/terms"            element={<Terms />} />
            <Route path="/privacy"          element={<Privacy />} />
            <Route path="/refund"           element={<Refund />} />
            <Route path="/contact"          element={<Contact />} />
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}
